import { useQueryClient } from "@tanstack/solid-query";
import { createFileRoute } from "@tanstack/solid-router";
import DocumentTable from "~/components/document/table/document-table";
import { UpgradePromptDialog } from "~/components/document/upgrade-prompt-dialog";
import { IconEllipsisVertical, IconPlus } from "~/components/icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import {
  type DocumentsParams,
  DocumentsResource,
} from "~/features/document/document.resource";
import { getDocumentsPageData } from "~/features/document/documents-page";
import { useDocumentUpload } from "~/features/document/use-document-upload";
import { withBusinessGuard } from "~/lib/route-guards";

export const Route = createFileRoute("/_dashboard/dashboard/documents/")({
  ...withBusinessGuard(),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;
    const defaultParams: DocumentsParams = { businessId, limit: 20 };
    const data = await getDocumentsPageData({ data: { businessId } });
    // seedIfEmpty (not seed) so a fresh client-side mutation (e.g. a rename
    // that optimistically updated the list cache) isn't clobbered by stale
    // loader data when navigating back to the table.
    DocumentsResource.seedIfEmpty(
      context.queryClient,
      defaultParams,
      data.documents,
    );
    businessDetailResource.seed(
      context.queryClient,
      businessId,
      data.businessDetail,
    );
    return {
      businessId,
      documents: data.documents,
      collection,
      activeBusiness: data.businessDetail,
    };
  },
  component: DocumentsPage,
});

function DocumentsPage() {
  const qc = useQueryClient();
  const data = Route.useLoaderData();
  const businessId = useActiveBusinessId();
  const { uploading, upgradeDialog, setUpgradeDialog, handleAddDocument } =
    useDocumentUpload();

  // Seed caches from loader data so client-side queries don't re-fetch
  const ld = data();
  DocumentsResource.seedIfEmpty(
    qc,
    { businessId: ld.businessId, limit: 20 },
    ld.documents,
  );
  businessDetailResource.seedIfEmpty(qc, ld.businessId, ld.activeBusiness);

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold">Documents</h1>
          <p class="text-muted-foreground">Manage your business documents.</p>
        </div>
        <DropdownMenu placement="bottom-end">
          <DropdownMenuTrigger
            as={Button<"button">}
            variant="outline"
            size="icon"
            disabled={uploading()}
          >
            <IconEllipsisVertical class="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-40">
            <DropdownMenuItem
              onClick={handleAddDocument}
              disabled={uploading()}
            >
              <IconPlus class="size-4" />
              {uploading() ? "Uploading..." : "Add Documents"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <DocumentTable businessId={businessId() ?? ""} />
      <UpgradePromptDialog
        open={upgradeDialog().open}
        message={upgradeDialog().message}
        onOpenChange={(open) => setUpgradeDialog((s) => ({ ...s, open }))}
      />
    </div>
  );
}
